import {
  FilterStore,
  RangeFilter,
  ValueFilter,
} from 'components/Filters/FilterStore'
import Checkbox from '../../Checkbox/checkbox'
import { useEffect } from 'react'
import { useRegisterFilterKey } from '../components/FilterModal'

interface QuickFilterProps {
  title: string
  valueLabel: string
  filterKey: string
  filterStore: FilterStore
}

interface QuickRangeFilterProps extends QuickFilterProps {
  predefinedValue: RangeFilter
}

export function QuickRangeFilter({
  title,
  valueLabel,
  filterKey,
  predefinedValue,
  filterStore,
}: QuickRangeFilterProps) {
  const {
    pendingRangeFilters: pending,
    setPendingRangeFilters: setPending,
    clearPendingRangeFilter: clearPending,
    registerFilterTitle,
  } = filterStore()

  const isActiveValueEqual = (a: RangeFilter, b: RangeFilter) => {
    if (!a || !b) return false
    return a.max == b.max && a.min == b.min
  }

  const isChecked: boolean = isActiveValueEqual(
    pending[filterKey],
    predefinedValue
  )

  const removeFilter = () => {
    clearPending(filterKey)
  }

  const addFilter = () => {
    setPending(filterKey, predefinedValue.min, predefinedValue.max)
  }

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useRegisterFilterKey(filterKey)

  return (
    <QuickFilter
      label={title}
      valueLabel={valueLabel}
      isChecked={isChecked}
      addFilter={addFilter}
      removeFilter={removeFilter}
    />
  )
}

interface QuickValueFilterProps extends QuickFilterProps {
  predefinedValue: ValueFilter[]
}

export function QuickValueFilter({
  title,
  valueLabel,
  filterKey,
  predefinedValue,
  filterStore,
}: QuickValueFilterProps) {
  const {
    pendingValueFilters: pending,
    setPendingValueFilters: setPending,
    clearPendingValueFilter: clearPending,
    registerFilterTitle,
  } = filterStore()

  const isActiveValueEqual = (a: ValueFilter[], b: ValueFilter[]) => {
    if (!a || !b) return false
    return a.length === b.length && a.every((v, i) => v.value == b[i].value)
  }

  const isChecked: boolean = isActiveValueEqual(
    pending[filterKey],
    predefinedValue
  )

  const removeFilter = () => {
    clearPending(filterKey)
  }

  const addFilter = () => {
    setPending(filterKey, predefinedValue)
  }

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useRegisterFilterKey(filterKey)

  return (
    <QuickFilter
      label={title}
      valueLabel={valueLabel}
      isChecked={isChecked}
      addFilter={addFilter}
      removeFilter={removeFilter}
    ></QuickFilter>
  )
}

function QuickFilter({
  label,
  valueLabel,
  isChecked,
  addFilter,
  removeFilter,
}: {
  label: string
  valueLabel: string
  isChecked: boolean
  addFilter: () => void
  removeFilter: () => void
}) {
  const onChange = () => {
    if (isChecked) {
      removeFilter()
    } else {
      addFilter()
    }
  }

  return (
    <label
      htmlFor={label}
      className="flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer text-sm text-neutral-700 hover:bg-neutral-100"
    >
      <Checkbox id={label} checked={isChecked} onChange={onChange} />
      <span className="select-none">
        {label}
        {valueLabel ? (
          <span className="text-neutral-500">: {valueLabel}</span>
        ) : null}
      </span>
    </label>
  )
}
